import { useEffect, useRef, useState } from 'react';

/**
 * Represents the configuration properties that can be used to control how a given boolean value
 * is dampened over time to prevent flickering and abrupt changes in its visibility.
 */
export type Dampening = {
  /**
   * The number of milliseconds for which the dampened value should remain hidden or visible after
   * the raw value changes its state rapidly.  This suppresses flicker by ensuring the dampened
   * value does not immediately flip when the raw value toggles quickly.
   *
   * Note: This property effectively sets both the value defined by 'hideDelayMs' and
   * 'showDelayMs' to the same duration - unless either of those is explicitly provided.
   */
  readonly changeDelayMs?: number;
  /**
   * The number of milliseconds for which the dampened value should remain visible after the raw
   * value changes from 'true' to 'false'.  This suppresses brief drops in the raw value such that
   * a raw value that changes from true -> false -> true very rapidly will result in a dampened
   * value that remains true, preventing flicker.
   */
  readonly hideDelayMs?: number;
  /**
   * The number of milliseconds for which the dampened value should remain visible once it has
   * appeared, ensuring that the indicator does not immediately disappear after being shown.
   * This ensures that once the indicator becomes visible, it remains so for at least this duration.
   */
  readonly minVisibleMs?: number;
  /**
   * The number of milliseconds for which the dampened value should remain hidden after the raw
   * value changes from 'false' to 'true'.  This suppresses brief rises in the raw value such that
   * a raw value that changes from false -> true -> false very rapidly will result in a dampened
   * value that remains false, preventing flicker.
   *
   * @default 0
   */
  readonly showDelayMs?: number;
};

export const DefaultDampening = {
  changeDelayMs: undefined,
  hideDelayMs: 100,
  minVisibleMs: 500,
  showDelayMs: 200,
} as const satisfies Dampening;

/**
 * Derives a dampened visibility flag from a raw {@link UseDampenedVisibilityParams.active} loading
 * signal, smoothing out flicker on both edges of the signal.
 *
 * A true-blip shorter than {@link UseDampenedVisibilityParams.showDelayMs} never shows the
 * indicator, and a false-blip shorter than {@link UseDampenedVisibilityParams.hideDelayMs} never
 * hides it once shown, so a signal that flips and reverts within either threshold is ignored.
 *
 * @returns {boolean} Whether the dampened indicator should currently be visible.
 */
export const useDampenedVisibility = (
  rawValue: boolean,
  dampening?: Dampening,
  options?: { readonly isDisabled?: boolean },
): boolean => {
  const showDelayMs =
    dampening?.showDelayMs ?? dampening?.changeDelayMs ?? DefaultDampening.showDelayMs;
  const hideDelayMs =
    dampening?.hideDelayMs ?? dampening?.changeDelayMs ?? DefaultDampening.hideDelayMs;
  const minVisibleMs = dampening?.minVisibleMs ?? DefaultDampening.minVisibleMs;

  const isDampeningDisabled =
    dampening === undefined ||
    (showDelayMs <= 0 && minVisibleMs <= 0 && hideDelayMs <= 0) ||
    options?.isDisabled;
  const [isVisible, setIsVisible] = useState(() => rawValue && showDelayMs <= 0);
  const shownAtRef = useRef<null | number>(null);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    if (isDampeningDisabled) {
      return cleanup;
    }
    if (rawValue) {
      if (isVisible) {
        shownAtRef.current ??= Date.now();
      } else if (showDelayMs <= 0) {
        shownAtRef.current = Date.now();
        setIsVisible(true);
      } else {
        const showTimeout = setTimeout(() => {
          shownAtRef.current = Date.now();
          setIsVisible(true);
        }, showDelayMs);
        cleanup = () => clearTimeout(showTimeout);
      }
    } else if (isVisible) {
      const remaining = minVisibleMs - (Date.now() - (shownAtRef.current ?? Date.now()));
      const hideDelay = Math.max(remaining, hideDelayMs);
      if (hideDelay <= 0) {
        shownAtRef.current = null;
        setIsVisible(false);
      } else {
        const hideTimeout = setTimeout(() => {
          shownAtRef.current = null;
          setIsVisible(false);
        }, hideDelay);
        cleanup = () => clearTimeout(hideTimeout);
      }
    }
    return cleanup;
  }, [rawValue, hideDelayMs, isDampeningDisabled, isVisible, minVisibleMs, showDelayMs]);

  return isDampeningDisabled ? rawValue : isVisible;
};
